import React, { useEffect, useState, useRef, useMemo } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { getUserList } from "../../redux/selector";
import apiServices from "../../services/service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LeftOptions from "./leftOptions";
import RightContentActionPermissions from "./rightContentActionPermissions";
import UiPermissions from "./uiPermissions";
import { Link } from "react-router-dom";
import { useAppAuth } from "../../auth/appAuthContext";
import AddTeamForm from "./AddTeamForm";
import DeleteConfirmationModal from "../../shared/modal/deleteConfirmation";
import Loader from "../../shared/component/Loader";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { RichSelectModule } from "@ag-grid-enterprise/rich-select";
import { ModuleRegistry } from "@ag-grid-community/core";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RangeSelectionModule,
  RowGroupingModule,
  RichSelectModule,
]);

const assignSelectStyles = {
  container: (base) => ({ ...base, fontSize: ".8rem" }),
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#456ABB" : base.borderColor,
    boxShadow: state.isFocused ? "0 0 0 1px #456ABB" : "none",
    "&:hover": { borderColor: "#456ABB" },
  }),
  valueContainer: (base) => ({ ...base, height: "32px", padding: "0 6px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#C2DBFF" : state.isFocused ? "#f0f5ff" : "white",
    color: "#222",
  }),
};

const MemberRoleCellRenderer = ({ data, context }) => {
  const { teamOwnerId, teamCaptainId } = context;
  const role =
    data.memberId === teamOwnerId ? "Owner" :
      data.memberId === teamCaptainId ? "Captain" : "Member";
  const colors = { Owner: "#28168d", Captain: "#601278", Member: "#444" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: "12px",
      fontSize: "0.75rem",
      fontWeight: 600,
      background: role === "Member" ? "#eee" : "linear-gradient(90deg, rgba(40,22,141,1) 0%, rgba(96,18,120,1) 100%)",
      color: role === "Member" ? "#444" : "#fff",
    }}>
      {role}
    </span>
  );
};

const MemberActionCellRenderer = ({ data, context }) => {
  const { handleRemoveMemberClick } = context;
  return (
    <img
      src="/assets/images/trash.png"
      className="action-img2"
      alt="Remove member"
      title="Remove member"
      style={{ cursor: "pointer" }}
      onClick={() => handleRemoveMemberClick(data)}
    />
  );
};

const AssignedToMemberCellRenderer = ({ data, context }) => {
  const { teamMemberOptions, handleAssignMemberChange, assignedToMemberMap } = context;
  const currentMemberId = assignedToMemberMap[data.id] ?? data.assignedToTeamMemberId;
  const currentMember =
    teamMemberOptions.find((m) => m.value?.toLowerCase() === currentMemberId?.toLowerCase()) ?? null;
  return (
    <Select
      options={teamMemberOptions}
      value={currentMember}
      onChange={(option) => handleAssignMemberChange(data.id, option)}
      placeholder="Select member..."
      isClearable
      menuPortalTarget={document.body}
      styles={assignSelectStyles}
    />
  );
};

function TeamManagement() {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);


  // Team state
  const [teamId, setTeamId] = useState();
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [dropItems, setDropItems] = useState([]);
  const [selected, setSelected] = useState();

  // Permissions state
  const [options, setOptions] = useState([]);
  const [showoptionsRoleManagement, setShowOptionsRoleManagement] = useState(options[0]);
  const [uiPermissionsModuleName, setUiPermissionsModuleName] = useState([]);
  const [activeOptionRoleManagement, setActiveOptionRoleManagement] = useState();
  const [uiPermissionsData, setUiPermissionsData] = useState();
  const [showoptionsUiPermissions, setShowOptionsUiPermissions] = useState();
  const [checkboxes, setCheckboxes] = useState();
  const [activeOptionUiPermissions, setActiveOptionUiPermissions] = useState();
  const [permissionForParticularRoleInSaveFormat, setPermissionForParticularRoleInSaveFormat] = useState();
  const [permissionByModule, setPermissionByModule] = useState();
  const [permissionId, setPermissionId] = useState([]);
  const [updatedPermissions, setUpdatePermissions] = useState();
  const [isRoleChange, setIsRoleChange] = useState(false);
  const [formatedUiPermissions, setFormatedUiPermissions] = useState();

  // Tabs: Members is first
  const [activeTab, setActiveTab] = useState("Members");

  // Members tab state — sourced from Redux (fetched once on home page)
  const usersList = useSelector(getUserList);

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [teamOwnerId, setTeamOwnerId] = useState(null);
  const [teamCaptainId, setTeamCaptainId] = useState(null);

  // Team Assignment tab state
  const [teamObjects, setTeamObjects] = useState([]);
  const [teamObjectsLoading, setTeamObjectsLoading] = useState(false);
  const [assignedToMemberMap, setAssignedToMemberMap] = useState({});

  // Lead tab state
  const [allLeads, setAllLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsToAssign, setLeadsToAssign] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [teamLeadsLoading, setTeamLeadsLoading] = useState(false);
  const [savingLeads, setSavingLeads] = useState(false);

  // Add Team modal
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);

  const { user: accountDetail } = useAppAuth();
  const dropdownRef = useRef(null);
  const teamAssignmentGridRef = useRef(null);
  const teamMembersGridRef = useRef(null);

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleSelectOptionRoleManagement = (option) => {
    setShowOptionsRoleManagement(option);
    setActiveOptionRoleManagement(option);
  };

  useEffect(() => {
    setActiveOptionUiPermissions(uiPermissionsModuleName[0]);
    setShowOptionsUiPermissions(uiPermissionsModuleName[0]);
  }, [uiPermissionsModuleName]);

  const handleTeamSelect = (team) => {
    setTeamsLoading(true);
    setSelected(team.teamName);
    setTeamId(team.id);
    setTeamOwnerId(team.teamOwnerId ?? null);
    setTeamCaptainId(team.teamCaptainId ?? null);
    setTeamMembers([]);
    setMembersToAdd([]);
    setLeadsToAssign([]);
    setTeamLeads([]);
    setTeamObjects([]);
    setAssignedToMemberMap({});
  };

  const fetchTeamMembers = (id) => {
    apiServices
      .getMembersByTeamId(id)
      .then((response) => {
        const data = response.data.team;
        setTeamMembers(data?.memberDetails || []);
        if (data?.teamOwnerId) setTeamOwnerId(data.teamOwnerId);
        if (data?.teamCaptainId) setTeamCaptainId(data.teamCaptainId);
      })
      .catch((error) => console.error("Error fetching team members:", error))
      .finally(() => setTeamsLoading(false));
  };

  useEffect(() => {
    if (teamId) fetchTeamMembers(teamId);
    else setTeamMembers([]);
  }, [teamId]);

  const handleSaveMembers = () => {
    if (!membersToAdd.length) return;
    setSavingMembers(true);

    // Include existing members so the API replace doesn't drop them
    const existingIds = teamMembers.map((m) => m.memberId);
    const newIds = membersToAdd.map((m) => m.value);
    const allUserIds = [...new Set([...existingIds, ...newIds])];

    apiServices
      .addMembersToTeam({ teamId, userIds: allUserIds })
      .then(() => {
        toast.success("Members added successfully!", { position: "top-right" });
        setMembersToAdd([]);
        fetchTeamMembers(teamId);
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || error.message || "Failed to add members",
          { position: "top-right" }
        );
      })
      .finally(() => setSavingMembers(false));
  };

  const handleRemoveMemberClick = (member) => {
    setMemberToRemove(member);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = () => {
    if (!memberToRemove) return;
    setShowRemoveConfirm(false);
    setRemovingMember(true);
    apiServices
      .removeTeamMember({ teamId, memberId: memberToRemove.memberId })
      .then(() => {
        toast.success("Member removed successfully!", { position: "top-right" });
        setTeamMembers((prev) => prev.filter((m) => m.memberId !== memberToRemove.memberId));
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error.message || "Failed to remove member", { position: "top-right" });
      })
      .finally(() => {
        setRemovingMember(false);
        setMemberToRemove(null);
      });
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsActive(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);


  // Fetch teams for the dropdown
  useEffect(() => {
    setTeamsLoading(true);
    apiServices
      .getTeams()
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.teamList || raw?.teams || raw?.data || [];
        setDropItems(data);
      })
      .catch((error) => console.error("Error fetching teams:", error))
      .finally(() => setTeamsLoading(false));
  }, []);

  // Load all permissions, then mark the team's assigned ones as checked
  // useEffect(() => {
  //   if (teamId) {
  //     setLoading(true);
  //     apiServices
  //       .getPermissionGroup()
  //       .then((pgResponse) => {
  //         const allPermissions = pgResponse.data.permissionList;
  //         const uniqueModules = [...new Set(allPermissions.map((p) => p.moduleName))];
  //         setOptions(uniqueModules);

  //         const moduleMap = {};
  //         allPermissions.forEach(({ moduleName, permissionDisplayName, permissionId }) => {
  //           if (!moduleMap[moduleName]) moduleMap[moduleName] = [];
  //           moduleMap[moduleName][permissionDisplayName] = { id: permissionId, ischecked: false };
  //         });

  //         return apiServices.getActionPermissionByTeamId(teamId).then((teamResponse) => {
  //           const teamPermissions = teamResponse.data.teamPermissions;
  //           const checkedIds = [];

  //           teamPermissions.forEach(({ permissionId }) => {
  //             checkedIds.push(permissionId);
  //             for (const mod in moduleMap) {
  //               for (const perm in moduleMap[mod]) {
  //                 if (moduleMap[mod][perm].id === permissionId) {
  //                   moduleMap[mod][perm].ischecked = true;
  //                 }
  //               }
  //             }
  //           });

  //           setPermissionId(Array.from(new Set(checkedIds)));
  //           setPermissionByModule(moduleMap);
  //           setUpdatePermissions(moduleMap);
  //           setShowOptionsRoleManagement(uniqueModules[0]);
  //           setActiveOptionRoleManagement(uniqueModules[0]);
  //           setLoading(false);
  //         });
  //       })
  //       .catch((error) => { console.error("Error:", error); setLoading(false); });
  //   }
  // }, [teamId, isRoleChange]);

  // Save action permissions
  const handleSaveActionPermissions = () => {
    setLoading(true);
    const data = { teamId, permissionIds: permissionId };
    apiServices
      .addActionPermissionsForTeam(data)
      .then((response) => {
        if (response.status === 200) {
          setIsRoleChange(true);
          toast.success("Action Permission updated successfully!", { position: "top-right" });
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        toast.error(`${error.message}`, { position: "top-right" });
        setLoading(false);
      });
  };

  const receiveDataFromChild = (data) => {
    if (data.length > 0) {
      if (permissionId.includes(data[0])) uncheckCheckbox(data[0]);
      else checkCheckbox(data[0]);
    }
  };

  const checkCheckbox = (id) => {
    setPermissionId((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const uncheckCheckbox = (id) => {
    setPermissionId((prev) => prev.filter((checkboxId) => checkboxId !== id));
  };

  // Refresh user detail after role/permission change
  useEffect(() => {
    if (accountDetail && isRoleChange) {
      apiServices
        .getUserDetail(accountDetail.localAccountId)
        .then((response) => {
          if (response.status === 200) {
            setIsRoleChange(false);
            localStorage.setItem("userInformation", JSON.stringify(response.data.user));
            const data = response.data.user.userPermissions;
            const uniquePermissionDisplayNames = [...new Set(data.map((p) => p.permissionValue))];
            const uniqueDeniedPermissionDisplayNames = [
              ...new Set(response.data.user.permissionsDenied.map((p) => p.permissionValue)),
            ];
            const filteredPermissions = uniquePermissionDisplayNames.filter(
              (p) => !uniqueDeniedPermissionDisplayNames.includes(p),
            );
            const uniqueGrantedPermissionDisplayNames = [
              ...new Set(response.data.user.permissionsGranted.map((p) => p.permissionValue)),
            ];
            uniqueGrantedPermissionDisplayNames.forEach((p) => {
              if (!filteredPermissions.includes(p)) filteredPermissions.push(p);
            });
            localStorage.setItem("accessPermissions", filteredPermissions);
          }
        })
        .catch(() => setLoading(false));
    }
  }, [isRoleChange]);

  // Fetch all UI permissions structure once (background) — only used for tree structure, not left panel
  useEffect(() => {
    apiServices
      .getUiPermissions()
      .then((response) => {
        const tmpData = response.data.uiPermissionList;
        const formattedData = {};
        tmpData.forEach((permission) => {
          const { moduleName } = permission;
          if (!formattedData[moduleName]) {
            formattedData[moduleName] = { menuPermissions: [], screens: [], fields: [] };
          }
          switch (permission.permissionTypeName) {
            case "Menu": formattedData[moduleName].menuPermissions.push(permission); break;
            case "Screen": formattedData[moduleName].screens.push(permission); break;
            case "Field": formattedData[moduleName].fields.push(permission); break;
            default: break;
          }
        });

        Object.values(formattedData).forEach((moduleData) => {
          moduleData.menuPermissions.forEach((menu) => {
            menu.screenPermissions = [];
            moduleData.screens.forEach((screen) => {
              if (screen.permissionParentId === menu.permissionId) {
                screen.fieldPermissions = [];
                moduleData.fields.forEach((field) => {
                  if (field.permissionParentId === screen.permissionId) screen.fieldPermissions.push(field);
                });
                menu.screenPermissions.push(screen);
              }
            });
          });
        });
        Object.keys(formattedData).forEach((moduleName) => {
          delete formattedData[moduleName].screens;
          delete formattedData[moduleName].fields;
        });
        setFormatedUiPermissions(formattedData);
      })
      .catch((error) => console.error("Error:", error));
  }, []);

  const handleSelectOptionUiPermissions = (option) => {
    setShowOptionsUiPermissions(option);
    setActiveOptionUiPermissions(option);
    if (uiPermissionsData?.hasOwnProperty(option)) setCheckboxes(uiPermissionsData[option]);
  };

  const handleCheckboxChange = (option, permission, permissionId) => {
    const updatedCheckboxes = { ...checkboxes };

    if (option === "menuPermissions") {
      const menuPermission = updatedCheckboxes.menuPermissions.find((menu) => menu.permissionId === permissionId);
      if (menuPermission) {
        menuPermission.isChecked = !menuPermission.isChecked;
        menuPermission.canView = !menuPermission.canView;
        if (!menuPermission.isChecked) {
          menuPermission.screenPermissions.forEach((screen) => {
            screen.isChecked = false;
            screen.canView = false;
            if (screen.fieldPermissions) {
              screen.fieldPermissions.forEach((field) => { field.isChecked = false; field.canEdit = false; field.canView = false; });
            }
          });
        }
      }
    } else if (option === "screenPermissions") {
      const menuPermission = updatedCheckboxes.menuPermissions.find(
        (menu) => menu.screenPermissions && menu.screenPermissions.some((screen) => screen.permissionId === permissionId),
      );
      if (menuPermission) { menuPermission.isChecked = true; menuPermission.canView = true; }
      const screenPermission = updatedCheckboxes.menuPermissions.reduce(
        (found, menu) => found || (menu.screenPermissions && menu.screenPermissions.find((screen) => screen.permissionId === permissionId)),
        null,
      );
      if (screenPermission) {
        screenPermission.isChecked = !screenPermission.isChecked;
        screenPermission.canView = !screenPermission.canView;
        if (!screenPermission.isChecked && screenPermission.fieldPermissions) {
          screenPermission.fieldPermissions.forEach((field) => { field.isChecked = false; field.canEdit = false; field.canView = false; });
        }
      }
      if (screenPermission?.isChecked && !screenPermission?.canView && screenPermission?.canEdit) screenPermission.canView = true;
    } else if (option === "fieldPermissions" || option === "canView" || option === "canEdit") {
      let fieldPermission, parentScreenPermission, parentMenuPermission;
      for (const menu of updatedCheckboxes.menuPermissions) {
        for (const screen of menu.screenPermissions) {
          if (screen.fieldPermissions) {
            fieldPermission = screen.fieldPermissions.find((field) => field.permissionId === permissionId);
            if (fieldPermission) { parentScreenPermission = screen; parentMenuPermission = menu; break; }
          }
        }
        if (fieldPermission) break;
      }
      if (fieldPermission) {
        if (option === "canEdit") fieldPermission.canEdit = !fieldPermission.canEdit;
        else if (option === "canView") fieldPermission.canView = !fieldPermission.canView;
        if (fieldPermission.canEdit && !fieldPermission.canView) fieldPermission.canView = true;
        if (fieldPermission.canEdit || fieldPermission.canView) {
          parentScreenPermission.isChecked = true;
          parentMenuPermission.isChecked = true;
          parentScreenPermission.canView = true;
          parentMenuPermission.canView = true;
        }
      }
    }

    setCheckboxes({ ...updatedCheckboxes });
    updateCheckBoxesForSaveFunction();
  };

  const updateCheckBoxesForSaveFunction = () => {
    const checkboxesData = checkboxes.menuPermissions;
    const permissionsData = permissionForParticularRoleInSaveFormat.teamUiPermissionsVm.teamAndUiPermissions;
    permissionsData.forEach((permission) => {
      checkboxesData.forEach((menuPermission) => {
        if (menuPermission.permissionId === permission.uiPermissionId) {
          permission.canCreate = menuPermission.canCreate;
          permission.canDelete = menuPermission.canDelete;
          permission.canEdit = menuPermission.canEdit;
          permission.canView = menuPermission.canView;
        } else {
          menuPermission.screenPermissions.forEach((screenPermission) => {
            if (screenPermission.permissionId === permission.uiPermissionId) {
              permission.canCreate = screenPermission.canCreate;
              permission.canDelete = screenPermission.canDelete;
              permission.canEdit = screenPermission.canEdit;
              permission.canView = screenPermission.canView;
            } else {
              screenPermission.fieldPermissions.forEach((fieldPermission) => {
                if (fieldPermission.permissionId === permission.uiPermissionId) {
                  permission.canCreate = fieldPermission.canCreate;
                  permission.canDelete = fieldPermission.canDelete;
                  permission.canEdit = fieldPermission.canEdit;
                  permission.canView = fieldPermission.canView;
                }
              });
            }
          });
        }
      });
    });
    setPermissionForParticularRoleInSaveFormat(permissionForParticularRoleInSaveFormat);
  };

  // Fetch UI permissions for selected team
  // useEffect(() => {
  //   if (teamId) {
  //     setLoading(true);
  //     apiServices
  //       .getUiPermissionByTeamId(teamId)
  //       .then((response) => {
  //         const tmpData = response.data.teamUiPermissions;

  //         // Derive left-panel modules only from this team's permissions
  //         const teamModules = [...new Set(tmpData.map((p) => p.moduleName))];
  //         setUiPermissionsModuleName(teamModules);

  //         // Filter the full tree to only the team's modules
  //         const uiPermissions = {};
  //         teamModules.forEach((mod) => {
  //           if (formatedUiPermissions?.[mod]) {
  //             uiPermissions[mod] = JSON.parse(JSON.stringify(formatedUiPermissions[mod]));
  //           }
  //         });

  //         // Mark isChecked / canCreate / canDelete / canView / canEdit from API response
  //         Object.keys(uiPermissions).forEach((moduleKey) => {
  //           uiPermissions[moduleKey].menuPermissions.forEach((menuPermission) => {
  //             menuPermission.isChecked = tmpData.some((p) => p.permissionId === menuPermission.permissionId);
  //             const menuMatch = tmpData.find((p) => p.permissionId === menuPermission.permissionId);
  //             if (menuMatch) {
  //               menuPermission.canCreate = menuMatch.canCreate;
  //               menuPermission.canDelete = menuMatch.canDelete;
  //               menuPermission.canEdit = menuMatch.canEdit;
  //               menuPermission.canView = menuMatch.canView;
  //             } else {
  //               menuPermission.canCreate = false;
  //               menuPermission.canDelete = false;
  //               menuPermission.canEdit = false;
  //               menuPermission.canView = false;
  //             }
  //             menuPermission.screenPermissions.forEach((screenPermission) => {
  //               screenPermission.isChecked = tmpData.some((p) => p.permissionId === screenPermission.permissionId);
  //               const screenMatch = tmpData.find((p) => p.permissionId === screenPermission.permissionId);
  //               if (screenMatch) {
  //                 screenPermission.canCreate = screenMatch.canCreate;
  //                 screenPermission.canDelete = screenMatch.canDelete;
  //                 screenPermission.canEdit = screenMatch.canEdit;
  //                 screenPermission.canView = screenMatch.canView;
  //               } else {
  //                 screenPermission.canCreate = false;
  //                 screenPermission.canDelete = false;
  //                 screenPermission.canEdit = false;
  //                 screenPermission.canView = false;
  //               }
  //               screenPermission.fieldPermissions.forEach((fieldPermission) => {
  //                 fieldPermission.isChecked = tmpData.some((p) => p.permissionId === fieldPermission.permissionId);
  //                 const fieldMatch = tmpData.find((p) => p.permissionId === fieldPermission.permissionId);
  //                 if (fieldMatch) {
  //                   fieldPermission.canCreate = fieldMatch.canCreate;
  //                   fieldPermission.canDelete = fieldMatch.canDelete;
  //                   fieldPermission.canEdit = fieldMatch.canEdit;
  //                   fieldPermission.canView = fieldMatch.canView;
  //                 } else {
  //                   fieldPermission.canCreate = false;
  //                   fieldPermission.canDelete = false;
  //                   fieldPermission.canEdit = false;
  //                   fieldPermission.canView = false;
  //                 }
  //               });
  //             });
  //           });
  //         });

  //         const convertToSaveFormat = (uiPerms) => {
  //           const teamAndUiPermissions = [];
  //           for (const moduleName in uiPerms) {
  //             if (uiPerms.hasOwnProperty(moduleName)) {
  //               uiPerms[moduleName].menuPermissions.forEach((menuPermission) => {
  //                 teamAndUiPermissions.push({ teamId, uiPermissionId: menuPermission.permissionId, canCreate: menuPermission.canCreate ?? false, canDelete: menuPermission.canDelete ?? false, canEdit: menuPermission.canEdit ?? false, canView: menuPermission.canView ?? false });
  //                 menuPermission.screenPermissions.forEach((screenPermission) => {
  //                   teamAndUiPermissions.push({ teamId, uiPermissionId: screenPermission.permissionId, canCreate: screenPermission.canCreate ?? false, canDelete: screenPermission.canDelete ?? false, canEdit: screenPermission.canEdit ?? false, canView: screenPermission.canView ?? false });
  //                   screenPermission.fieldPermissions.forEach((fieldPermission) => {
  //                     teamAndUiPermissions.push({ teamId, uiPermissionId: fieldPermission.permissionId, canCreate: fieldPermission.canCreate ?? false, canDelete: fieldPermission.canDelete ?? false, canEdit: fieldPermission.canEdit ?? false, canView: fieldPermission.canView ?? false });
  //                   });
  //                 });
  //               });
  //             }
  //           }
  //           return { teamUiPermissionsVm: { teamAndUiPermissions } };
  //         };

  //         setPermissionForParticularRoleInSaveFormat(convertToSaveFormat(uiPermissions));
  //         setUiPermissionsData(uiPermissions);
  //         setCheckboxes(uiPermissions[teamModules[0]]);
  //         setLoading(false);
  //       })
  //       .catch((error) => { console.error("Error:", error); setLoading(false); });
  //   }
  // }, [teamId]);


  // Re-fetch teams list
  const refreshTeams = () => {
    setTeamsLoading(true);
    apiServices
      .getTeams()
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.teamList || raw?.teams || raw?.data || [];
        setDropItems(data);
      })
      .catch((error) => console.error("Error fetching teams:", error))
      .finally(() => setTeamsLoading(false));
  };

  // Add Team — toast & API handled in AddTeamForm; just close and refresh
  const handleTeamAdded = () => {
    setShowAddTeamModal(false);
    refreshTeams();
  };

  // Fetch all leads once on mount (for the assign dropdown)
  useEffect(() => {
    setLeadsLoading(true);
    apiServices
      .getLeadList({ pageSize: 500, pageNumber: 1, columnName: "UpdatedDateTime", orderType: "desc", filterJson: null })
      .then((response) => setAllLeads(response.data.leads || []))
      .catch((error) => console.error("Error fetching leads:", error))
      .finally(() => setLeadsLoading(false));
  }, []);

  // Fetch leads assigned to the selected team
  useEffect(() => {
    if (!teamId) return;
    setTeamLeadsLoading(true);
    apiServices
      .getLeadsByTeam(teamId)
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.leads || raw?.data || [];
        setTeamLeads(data);
      })
      .catch((error) => console.error("Error fetching team leads:", error))
      .finally(() => setTeamLeadsLoading(false));
  }, [teamId]);

  // Fetch team objects when teamId changes or user navigates to Object Assignment tab
  useEffect(() => {
    if (!teamId || activeTab !== "Object Assignment") return;
    setTeamObjectsLoading(true);
    apiServices
      .getTeamObjects(teamId)
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.teamObjects || raw?.data || [];
        setTeamObjects(data);
        const initialMap = {};
        data.forEach((obj) => {
          if (obj.assignedToTeamMemberId) initialMap[obj.id] = obj.assignedToTeamMemberId;
        });
        setAssignedToMemberMap(initialMap);
      })
      .catch((error) => console.error("Error fetching team objects:", error))
      .finally(() => setTeamObjectsLoading(false));
  }, [teamId, activeTab]);

  const leadOptions = useMemo(
    () =>
      allLeads.map((lead) => ({
        value: lead.id,
        label:
          `${lead.firstName || ""} ${lead.lastName || ""}`.trim() ||
          lead.companyName ||
          lead.email ||
          lead.id,
        meta: { company: lead.companyName, email: lead.email },
      })),
    [allLeads]
  );

  const availableLeadOptions = useMemo(() => {
    const assignedIds = new Set(teamLeads.map((l) => l.objectId?.toLowerCase()));
    return leadOptions.filter((opt) => !assignedIds.has(opt.value?.toLowerCase()));
  }, [leadOptions, teamLeads]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 120,
  }), []);

  const teamMembersColDefs = useMemo(() => [
    {
      headerName: "Name",
      minWidth: 160,
      flex: 1.2,
      valueGetter: (p) =>
        `${p.data.firstName || ""} ${p.data.lastName || ""}`.trim() || p.data.userName || "-",
    },
    {
      headerName: "Email / Username",
      minWidth: 200,
      flex: 1.5,
      valueGetter: (p) => p.data.email || p.data.userName || "-",
    },
    {
      headerName: "Team Role",
      minWidth: 120,
      maxWidth: 160,
      flex: 0.7,
      cellRenderer: MemberRoleCellRenderer,
      sortable: false,
      filter: false,
    },
    {
      headerName: "Action",
      minWidth: 80,
      maxWidth: 100,
      flex: 0.4,
      cellRenderer: MemberActionCellRenderer,
      sortable: false,
      filter: false,
    },
  ], []);

  useEffect(() => {
    if (teamMembersGridRef.current?.api) {
      teamMembersGridRef.current.api.refreshCells({ force: true });
    }
  }, [teamOwnerId, teamCaptainId]);

  const teamLeadsColDefs = useMemo(() => [
    {
      headerName: "Name",
      minWidth: 150,
      flex: 1,
      valueGetter: (p) =>
        p.data.fullName ||
        `${p.data.firstName || ""} ${p.data.lastName || ""}`.trim() ||
        "-",
    },
    {
      field: "companyName",
      headerName: "Company",
      minWidth: 150,
      flex: 1,
      valueFormatter: (p) => p.value || "-",
    },
    { field: "objectType", headerName: "Object Type", minWidth: 120, maxWidth: 150, flex: 0.6 },
    {
      field: "email",
      headerName: "Email",
      minWidth: 180,
      flex: 1.2,
      valueFormatter: (p) => p.value || "-",
    },
    {
      headerName: "Phone",
      minWidth: 130,
      flex: 0.8,
      valueGetter: (p) => p.data.phone || p.data.directPhone || p.data.mobile || "-",
    },
    {
      field: "owner",
      headerName: "Owner",
      minWidth: 130,
      flex: 0.8,
      valueFormatter: (p) => p.value || "-",
    },
  ], []);

  const teamMemberOptions = useMemo(
    () =>
      teamMembers.map((m) => ({
        value: m.memberId,
        label: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.userName || m.email || "",
      })),
    [teamMembers]
  );

  const handleAssignMemberChange = (objectId, selectedOption) => {
    setAssignedToMemberMap((prev) => ({ ...prev, [objectId]: selectedOption?.value ?? null }));
    apiServices
      .assignMemberToTeam({ id: objectId, assignedToTeamMemberId: selectedOption?.value ?? null })
      .then(() => toast.success("Member assigned successfully!", { position: "top-right" }))
      .catch((error) =>
        toast.error(
          error?.response?.data?.message || error.message || "Failed to assign member",
          { position: "top-right" }
        )
      );
  };

  useEffect(() => {
    if (teamAssignmentGridRef.current?.api) {
      teamAssignmentGridRef.current.api.refreshCells({ force: true });
    }
  }, [teamMemberOptions, assignedToMemberMap]);

  const teamAssignmentColDefs = useMemo(() => [
    {
      field: "objectId",
      headerName: "Object ID",
      minWidth: 160,
      maxWidth: 260,
      flex: 1,
      tooltipField: "objectId",
      cellStyle: { fontSize: "0.78rem", color: "#666" },
    },
    { field: "objectType", headerName: "Object Type", minWidth: 120, maxWidth: 160, flex: 0.6 },
    { field: "objectName", headerName: "Object Name", minWidth: 180, flex: 1.5, valueFormatter: (p) => p.value || "-" },
    {
      headerName: "Assigned To",
      minWidth: 240,
      flex: 1.5,
      cellRenderer: AssignedToMemberCellRenderer,
      sortable: false,
      filter: false,
    },
  ], []);

  const handleSaveLeads = () => {
    if (!leadsToAssign.length || !teamId) return;
    setSavingLeads(true);
    apiServices
      .addMappingToTeam({
        teamId,
        objectType: "Lead",
        assignedToTeamMemberId: null,
        objectIds: leadsToAssign.map((l) => l.value),
      })
      .then(() => {
        toast.success("Leads assigned successfully!", { position: "top-right" });
        setLeadsToAssign([]);
        setTeamLeadsLoading(true);
        return apiServices.getLeadsByTeam(teamId);
      })
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.leads || raw?.data || [];
        setTeamLeads(data);
      })
      .catch((error) =>
        toast.error(
          error?.response?.data?.message || error.message || "Failed to assign leads",
          { position: "top-right" }
        )
      )
      .finally(() => {
        setSavingLeads(false);
        setTeamLeadsLoading(false);
      });
  };

  return (
    <>
      {teamsLoading && <Loader />}
      {removingMember && <Loader />}
      <div className="inner-page" style={containerStyle}>
        <div className="dash-container">
          <div className="tbl_container">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12">
                <div className="main-hd">
                  <div>
                    <div className="pagetitle">
                      <img src="../assets/images/role-management-icons/left-arrow.png" alt="" />{" "}
                      Team-Management
                    </div>
                    <div className="brdText">
                      <Link to="/home" title="Home">Home</Link>{" "}
                      <span className="hs">/</span>{" "}
                      <span className="breadcrumb-without-link" title="Settings">Settings</span>
                      <span className="hs">/</span>{" "}
                      <span title="Team Management" className="curr">Team-Management</span>
                    </div>
                  </div>

                  <div style={{ position: "relative" }}>
                    {teamsLoading && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 10,
                          cursor: "not-allowed",
                          backgroundColor: "transparent",
                        }}
                      />
                    )}
                    <div className="graydiv">
                      <div className="div2">
                        <div className="para">
                          <p>Team:&nbsp;&nbsp;</p>
                          <div className="dropdown" onClick={() => setIsActive(!isActive)} ref={dropdownRef}>
                            <div className="dropdown-btn">{selected || "Select a Team"}</div>
                            <span className="fas fa-caret-down"><i className="bi bi-chevron-down"></i></span>
                            {isActive && (
                              <div className="dropdown-content">
                                {dropItems.length > 0 ? (
                                  dropItems.map((teams) => (
                                    <div
                                      className="dropdown-item"
                                      key={teams.id}
                                      onClick={() => { handleTeamSelect(teams); setIsActive(false); }}
                                    >
                                      {teams.teamName}
                                    </div>
                                  ))
                                ) : (
                                  <div className="dropdown-item" style={{ color: "#999", pointerEvents: "none" }}>
                                    No teams — click Add Team
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <button className="add-btn" type="button" onClick={() => setShowAddTeamModal(true)}>
                      + Add Team
                    </button>
                  </div>
                </div>

                <div className="test">
                  {loading ? (
                    <div className="custom-loading-overlay">
                      <div className="custom-loading-image"></div>
                    </div>
                  ) : (
                    <>
                      <div className="roleManagement-nav">
                        <ul className="nav nav-underline">
                          <li className="nav-item">
                            <a
                              className={`nav-link ${activeTab === "Members" ? "active" : ""}`}
                              onClick={() => handleTabClick("Members")}
                            >
                              Members
                            </a>
                          </li>
                          {/* <li className="nav-item">
                            <a
                              className={`nav-link ${activeTab === "Permissions" ? "active" : ""}`}
                              onClick={() => handleTabClick("Permissions")}
                            >
                              Permissions
                            </a>
                          </li> */}
                          {/* <li className="nav-item">
                            <a
                              className={`nav-link ${activeTab === "UI permissions" ? "active" : ""}`}
                              onClick={() => handleTabClick("UI permissions")}
                            >
                              UI Visibility
                            </a>
                          </li> */}
                          <li className="nav-item">
                            <a
                              className={`nav-link ${activeTab === "Leads" ? "active" : ""}`}
                              onClick={() => handleTabClick("Leads")}
                            >
                              Leads
                            </a>
                          </li>
                          <li className="nav-item">
                            <a
                              className={`nav-link ${activeTab === "Object Assignment" ? "active" : ""}`}
                              onClick={() => handleTabClick("Object Assignment")}
                            >
                              Object Assignment
                            </a>
                          </li>
                        </ul>
                      </div>

                      <div>
                        {/* Members Tab */}
                        {activeTab === "Members" && (
                          selected && selected.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 210px)", padding: "16px", gap: "12px", boxSizing: "border-box" }}>
                              {/* Add Members section */}
                              <div style={{ flexShrink: 0, display: "flex", alignItems: "flex-end", gap: "12px" }}>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                                    Add Members
                                  </label>
                                  <Select
                                    isMulti
                                    options={usersList.map((u) => ({
                                      value: u.userId,
                                      label:
                                        `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                                        u.userName ||
                                        u.emailAddress ||
                                        u.email ||
                                        "",
                                    }))}
                                    value={membersToAdd}
                                    onChange={setMembersToAdd}
                                    placeholder="Search and select members..."
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    menuPortalTarget={document.body}
                                    styles={{
                                      container: (base) => ({ ...base, fontSize: ".8rem" }),
                                      control: (base, state) => ({
                                        ...base,
                                        borderColor: state.isFocused ? "#456ABB" : base.borderColor,
                                        boxShadow: state.isFocused ? "0 0 5px rgba(0,86,179,0.3)" : "none",
                                        "&:hover": { borderColor: "#456ABB" },
                                      }),
                                      menu: (base) => ({ ...base, zIndex: 9999 }),
                                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                      option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected ? "#C2DBFF" : state.isFocused ? "#f0f5ff" : "white",
                                        color: "#222",
                                        fontSize: ".8rem",
                                      }),
                                      multiValue: (base) => ({ ...base, backgroundColor: "#dde8ff", borderRadius: "4px" }),
                                      multiValueLabel: (base) => ({ ...base, fontSize: ".75rem", color: "#275ACA" }),
                                    }}
                                  />
                                </div>
                                <button
                                  className="add-btn"
                                  type="button"
                                  onClick={handleSaveMembers}
                                  disabled={savingMembers || !membersToAdd.length}
                                >
                                  {savingMembers ? "Saving..." : "Save Members"}
                                </button>
                              </div>

                              {/* Members AG Grid */}
                              <div style={{ flex: 1, minHeight: 0 }}>
                                <div className="ag-theme-balham" style={{ height: "100%", width: "100%" }}>
                                  <AgGridReact
                                    ref={teamMembersGridRef}
                                    rowData={teamMembers}
                                    columnDefs={teamMembersColDefs}
                                    defaultColDef={defaultColDef}
                                    context={{ teamOwnerId, teamCaptainId, handleRemoveMemberClick }}
                                    rowHeight={42}
                                    animateRows
                                    suppressRowClickSelection
                                    alwaysShowVerticalScroll
                                    onGridReady={(params) => params.api.sizeColumnsToFit()}
                                    overlayNoRowsTemplate="<span style='color:#999'>No members in this team yet. Add members above.</span>"
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="role-not-selected">
                              <h1>Please select a team from the Team&apos;s dropdown</h1>
                            </div>
                          )
                        )}

                        {/* Action Permissions Tab */}
                        {/* {activeTab === "Permissions" && (
                          selected && selected.length > 0 ? (
                            <div className="parentdiv">
                              <div className="whitediv">
                                <LeftOptions
                                  options={options}
                                  onSelectOption={handleSelectOptionRoleManagement}
                                  activeOption={activeOptionRoleManagement}
                                />
                                <div className="UIcontentBlock">
                                  {showoptionsRoleManagement !== undefined ? (
                                    <RightContentActionPermissions
                                      selectedOption={showoptionsRoleManagement}
                                      permissionByModule={updatedPermissions}
                                      permissionId={permissionId}
                                      sendDataToParent={receiveDataFromChild}
                                    />
                                  ) : (
                                    <RightContentActionPermissions
                                      selectedOption={options[0]}
                                      permissionByModule={permissionByModule}
                                      permissionId={permissionId}
                                      sendDataToParent={receiveDataFromChild}
                                    />
                                  )}
                                  <div style={{ marginRight: "20px", float: "right" }}>
                                    <button className="add-btn" type="button" onClick={handleSaveActionPermissions}>
                                      Save
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="role-not-selected">
                              <h1>Please select a team from the Team&apos;s dropdown</h1>
                            </div>
                          )
                        )} */}

                        {/* UI Visibility Tab */}
                        {/* {activeTab === "UI Visibility" && (
                          selected && selected.length > 0 ? (
                            <div className="parentdiv">
                              <UiPermissions
                                options={options}
                                onSelectOption={handleSelectOptionUiPermissions}
                                activeOption={activeOptionRoleManagement}
                                roleId={teamId}
                                formatedUiPermissions={formatedUiPermissions}
                                uiPermissionsModuleName={uiPermissionsModuleName}
                                uiPermissionsData={uiPermissionsData}
                                showoptionsUiPermissions={showoptionsUiPermissions}
                                handleSelectOptionUiPermissions={handleSelectOptionUiPermissions}
                                activeOptionUiPermissions={activeOptionUiPermissions}
                                checkbox={checkboxes}
                                handleCheckboxChange={handleCheckboxChange}
                                permissionForParticularRoleInSaveFormat={permissionForParticularRoleInSaveFormat}
                              />
                            </div>
                          ) : (
                            <div className="role-not-selected">
                              <h1>Please select a team from the Team&apos;s dropdown</h1>
                            </div>
                          )
                        )} */}

                        {/* Lead Tab */}
                        {activeTab === "Leads" && (
                          selected && selected.length > 0 ? (
                            <div className="parentdiv" style={{ padding: "16px", display: "flex", flexDirection: "column", height: "calc(100vh - 210px)" }}>
                              {/* Assign leads section */}
                              <div style={{ marginBottom: "16px", display: "flex", alignItems: "flex-end", gap: "12px", flexShrink: 0 }}>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                                    Assign Leads
                                  </label>
                                  <Select
                                    isMulti
                                    isLoading={leadsLoading}
                                    options={availableLeadOptions}
                                    value={leadsToAssign}
                                    onChange={setLeadsToAssign}
                                    placeholder="Search and select leads..."
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    menuPortalTarget={document.body}
                                    formatOptionLabel={(opt) => (
                                      <div style={{ fontSize: ".8rem" }}>
                                        <div style={{ fontWeight: 500 }}>{opt.label}</div>
                                        {(opt.meta?.company || opt.meta?.email) && (
                                          <div style={{ color: "#888", fontSize: ".73rem" }}>
                                            {[opt.meta.company, opt.meta.email].filter(Boolean).join(" · ")}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    styles={{
                                      container: (base) => ({ ...base, fontSize: ".8rem" }),
                                      control: (base, state) => ({
                                        ...base,
                                        borderColor: state.isFocused ? "#456ABB" : base.borderColor,
                                        boxShadow: state.isFocused ? "0 0 5px rgba(0,86,179,0.3)" : "none",
                                        "&:hover": { borderColor: "#456ABB" },
                                      }),
                                      menu: (base) => ({ ...base, zIndex: 9999 }),
                                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                      option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected ? "#C2DBFF" : state.isFocused ? "#f0f5ff" : "white",
                                        color: "#222",
                                      }),
                                      multiValue: (base) => ({ ...base, backgroundColor: "#dde8ff", borderRadius: "4px" }),
                                      multiValueLabel: (base) => ({ ...base, fontSize: ".75rem", color: "#275ACA" }),
                                    }}
                                  />
                                </div>
                                <button
                                  className="add-btn"
                                  type="button"
                                  onClick={handleSaveLeads}
                                  disabled={savingLeads || !leadsToAssign.length}
                                >
                                  {savingLeads ? "Saving..." : "Save Leads"}
                                </button>
                              </div>

                              {/* Assigned leads grid */}
                              {teamLeadsLoading ? (
                                <div className="text-center py-4" style={{ color: "#999" }}>Loading...</div>
                              ) : (
                                <div style={{ flex: 1, minHeight: 0 }}>
                                  <div className="ag-theme-balham" style={{ height: "100%", width: "100%" }}>
                                    <AgGridReact
                                      rowData={teamLeads}
                                      columnDefs={teamLeadsColDefs}
                                      defaultColDef={defaultColDef}
                                      rowHeight={42}
                                      animateRows
                                      suppressRowClickSelection
                                      alwaysShowVerticalScroll
                                      onGridReady={(params) => params.api.sizeColumnsToFit()}
                                      overlayNoRowsTemplate="<span style='color:#999'>No leads assigned to this team yet.</span>"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="role-not-selected">
                              <h1>Please select a team from the Team&apos;s dropdown</h1>
                            </div>
                          )
                        )}
                        {/* Team Assignment Tab */}
                        {activeTab === "Object Assignment" && (
                          selected && selected.length > 0 ? (
                            teamObjectsLoading ? (
                              <div className="text-center py-4" style={{ color: "#999" }}>Loading...</div>
                            ) : (
                              <div
                                className="ag-theme-balham"
                                style={{ height: "calc(100vh - 210px)", width: "100%" }}
                              >
                                <AgGridReact
                                  ref={teamAssignmentGridRef}
                                  rowData={teamObjects}
                                  columnDefs={teamAssignmentColDefs}
                                  defaultColDef={defaultColDef}
                                  context={{ teamMemberOptions, handleAssignMemberChange, assignedToMemberMap }}
                                  rowHeight={42}
                                  animateRows
                                  suppressRowClickSelection
                                  alwaysShowVerticalScroll
                                  tooltipShowDelay={300}
                                  onGridReady={(params) => params.api.sizeColumnsToFit()}
                                  overlayNoRowsTemplate="<span style='color:#999'>No objects assigned to this team yet.</span>"
                                />
                              </div>
                            )
                          ) : (
                            <div className="role-not-selected">
                              <h1>Please select a team from the Team&apos;s dropdown</h1>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        show={showRemoveConfirm}
        handleClose={() => { setShowRemoveConfirm(false); setMemberToRemove(null); }}
        handleConfirm={handleConfirmRemove}
        content="team member"
      />

      <AddTeamForm
        open={showAddTeamModal}
        onClose={() => setShowAddTeamModal(false)}
        onSave={handleTeamAdded}
        usersList={usersList}
      />
    </>
  );
}

export default TeamManagement;
